<script setup lang="ts">
import TaskClientResponseThread from '@/components/tasks/TaskClientResponseThread.vue';
import { Switch } from '@/components/ui/switch';
import { Eye, EyeOff } from 'lucide-vue-next';

defineProps<{
  clientVisible: boolean;
  responseRequired: boolean;
  responsePrompt: string;
  responseStatus: string;
  responseThread: any[];
  followUpMessage: string;
  requestingFollowUp: boolean;
}>();

const emit = defineEmits<{
  (event: 'update:client-visible', value: boolean): void;
  (event: 'update:response-required', value: boolean): void;
  (event: 'update:response-prompt', value: string): void;
  (event: 'save:response-prompt'): void;
  (event: 'update:follow-up-message', value: string): void;
  (event: 'request-follow-up'): void;
}>();
</script>

<template>
  <section>
    <div class="mb-3">
      <p class="text-base font-semibold text-foreground">Sharing</p>
      <p class="text-sm text-muted-foreground">Control whether this task appears in the client portal and whether the client needs to respond.</p>
    </div>

    <div class="divide-y divide-border/60 border-y border-border/70">
      <div class="flex items-start justify-between gap-3 py-5">
        <div class="min-w-0">
          <div class="inline-flex items-center gap-2">
            <component :is="clientVisible ? Eye : EyeOff" class="size-3.5 text-muted-foreground/75" />
            <p class="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground/80">Portal visibility</p>
          </div>
          <p class="mt-2 text-sm font-medium">{{ clientVisible ? 'Visible in client portal' : 'Private to workspace' }}</p>
          <p class="mt-1 text-xs text-muted-foreground">Shared tasks can be seen by the client without exposing internal scheduling controls.</p>
        </div>
        <Switch :checked="clientVisible" @update:checked="value => emit('update:client-visible', value)" />
      </div>

      <div class="py-5">
        <div class="flex items-start justify-between gap-3">
          <div>
            <label class="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground/85">Client response</label>
            <p class="mt-2 text-sm font-medium">Request a response in the portal</p>
            <p class="mt-1 text-xs text-muted-foreground">Let the client submit notes and uploads without editing the task itself.</p>
          </div>
          <Switch :checked="responseRequired" :disabled="!clientVisible" @update:checked="value => emit('update:response-required', value)" />
        </div>

        <div v-if="!clientVisible" class="mt-3 border-t border-dashed border-border/70 pt-3 text-xs text-muted-foreground">
          Make this task visible in the client portal before requesting a response.
        </div>

        <div v-else class="mt-4 space-y-3">
          <textarea
            :value="responsePrompt"
            rows="4"
            class="w-full rounded-xl border border-border/70 bg-background/84 px-3 py-2 text-sm shadow-none"
            :disabled="!responseRequired"
            placeholder="Tell the client exactly what you need from them, such as files, copy, approval notes, or confirmations."
            @input="emit('update:response-prompt', ($event.target as HTMLTextAreaElement).value)"
            @blur="emit('save:response-prompt')"
          />

          <TaskClientResponseThread
            :response-status="responseStatus"
            :thread="responseThread"
            empty-state="No client response thread yet. Once you request information, the conversation will show here."
            :show-follow-up-composer="true"
            :follow-up-message="followUpMessage"
            :requesting-follow-up="requestingFollowUp"
            @update:follow-up-message="emit('update:follow-up-message', $event)"
            @request-follow-up="emit('request-follow-up')"
          />
        </div>
      </div>
    </div>
  </section>
</template>
